/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Category, Product, Shops } from '@infrab4a/connect';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { TableColumn } from 'src/app/components/table';
import { ShopCategoryService } from 'src/app/connect-api/api/shop/shop-category.service';
import { getShopsArray } from 'src/app/connect-api/enums/ShopMap';
import { Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { buildMostRelevantsByShop } from './most-relevants-selection.util';

@Component({
  selector: 'app-shop-category-detail',
  templateUrl: './shop-category-detail.component.html',
  styleUrl: './shop-category-detail.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ShopCategoryDetailComponent implements AfterViewInit, OnInit {
  @Input() categoryId?: string;
  private title = inject(Title);
  private shoppingCategoryService = inject(ShopCategoryService);
  private messageService = inject(MessageService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private permissionsService = inject(NgxPermissionsService);
  private confirmationService = inject(ConfirmationService);

  category: Partial<Category>;
  categoryProducts: Partial<Product>[] = [];
  productCols = [
    new TableColumn(
      'SKU',
      'sku',
      true,
      'text',
      '/shop-products/catalog/product/',
      'id',
      true,
      'contains'
    ),
    new TableColumn(
      'EAN',
      'EAN',
      true,
      'text',
      '/shop-products/catalog/product/',
      'id'
    ),
    new TableColumn(
      'Nome',
      'name',
      true,
      'text',
      '/shop-products/catalog/product/',
      'id',
      true,
      'contains'
    ),
    new TableColumn(
      'Marca',
      'brand',
      true,
      'text',
      undefined,
      undefined,
      true,
      'contains'
    )
  ];
  categoryTreeBreadCrumb = [{ label: 'Não possui árvore de categoria' }];
  mostRelevants: Record<string, Partial<Product>[]> = Object.fromEntries(
    getShopsArray().map((shop) => [shop.value, []])
  );
  selectedTab = 0;
  pendingRequest = false;
  running = false;
  shops = getShopsArray();
  accordionIndex = -1;
  productsForSelection: { [shop: string]: Partial<Product>[] } =
    Object.fromEntries(getShopsArray().map((shop) => [shop.value, []]));
  copyMostRelevants = new FormControl(false);
  permission = false;

  async ngOnInit(): Promise<void> {
    this.permission = await this.permissionsService.hasPermission([
      roleAsObject(Role.Full_Administrator).enumValue,
      roleAsObject(Role.Portfolio).enumValue
    ]);
  }

  async ngAfterViewInit(): Promise<void> {
    LoaderService.showLoader();
    try {
      if (this.categoryId) {
        this.category = await this.shoppingCategoryService.getCategoryById(
          this.categoryId
        );
        this.findProducts();
        this.title.setTitle(`Loja - ${this.category.name}`);
      } else {
        this.title.setTitle('Loja - Nova categoria');
        this.category = null;
      }
      this.activatedRoute.queryParams.subscribe((queryParams) => {
        if (queryParams['tab']) {
          this.selectedTab = Number(queryParams['tab']) || 0;
        } else if (!this.category) {
          this.selectedTab = 0;
        }
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async updateCategory(
    category: Partial<Category>,
    refresh = false
  ): Promise<void> {
    category = await this.shoppingCategoryService.updateCategory(
      ShopCategoryService.categoryUpdateDTO(category)
    );
    category.updatedAt = new Date();
    if (refresh) {
      this.category = await this.shoppingCategoryService.getCategoryById(
        category.id
      );
    } else {
      this.category = category;
    }
    this.startMostRelevants();
  }

  async saveMostRelevants(shop: Shops): Promise<void> {
    try {
      LoaderService.showLoader();
      const { metadata, ...category } = ShopCategoryService.categoryUpdateDTO(
        this.category
      );
      if (!category.mostRelevants)
        category.mostRelevants = { Glamshop: [], mensmarket: [] };

      this.shops.forEach((s) => {
        if (
          !category?.shops?.includes(s.value) ||
          (!this.copyMostRelevants.value &&
            !this.mostRelevants[s.value]?.length)
        )
          category.mostRelevants[s.value] = null;
        else if (this.copyMostRelevants.value)
          category.mostRelevants[s.value] = (this.mostRelevants[shop] ?? [])
            .filter(Boolean)
            .map((p) => p?.id);
        else
          category.mostRelevants[s.value] = (this.mostRelevants[s.value] ?? [])
            .filter(Boolean)
            .map((p) => p?.id);
      });
      await this.updateCategory(category);
      this.accordionIndex = -1;
      this.messageService.add({
        severity: 'success',
        detail: 'Ordenação dos produtos salva com sucesso.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  tabChanged($event: number): void {
    this.router.navigate(
      [`/shop-products/categories/category/${this.categoryId}`],
      {
        queryParams: { tab: $event },
        queryParamsHandling: 'merge'
      }
    );
  }

  categoryUpdated(category: Partial<Category>): void {
    if (
      this.category.brandCategory !== category.brandCategory ||
      this.category.isCollection !== category.isCollection
    )
      this.findProducts();
    this.category = category;
  }

  findProducts(): void {
    delete this.categoryProducts;
    this.runPromise(
      this.shoppingCategoryService.getCategoryProducts(this.category)
    );
  }

  private runPromise(promise: Promise<any>): void {
    if (!this.running) {
      this.running = true;
      setTimeout(async () => {
        try {
          const products = await promise;
          this.running = false;
          if (!this.pendingRequest) {
            this.products = products.products.data.map((product) => {
              return {
                ...product,
                images: product.miniatures?.length
                  ? [product.miniatures[0]]
                  : ['assets/images/no_img_available.jpg']
              };
            });
          } else {
            this.pendingRequest = false;
            this.findProducts();
          }
        } catch (error) {
          this.running = false;
          if (this.pendingRequest) this.findProducts();
          else this.products = [];
        }
      }, 1000);
    } else {
      this.pendingRequest = true;
    }
  }

  private startMostRelevants(): void {
    const shops = Array.isArray(this.shops) ? this.shops : [];
    const built = buildMostRelevantsByShop(
      shops,
      this.categoryProducts,
      this.category?.mostRelevants
    );

    shops.forEach((shop) => {
      if (!shop?.value) return;
      this.mostRelevants[shop.value] = built.mostRelevants[shop.value] ?? [];
      this.productsForSelection[shop.value] =
        built.productsForSelection[shop.value] ?? [];
    });

    this.syncCopyMostRelevantsFlag();
  }

  private syncCopyMostRelevantsFlag(): void {
    const shops = this.category?.shops;
    const mostRelevants = this.category?.mostRelevants;
    const referenceShop = this.category?.shop;
    const reference = Array.isArray(mostRelevants?.[referenceShop])
      ? mostRelevants[referenceShop]
      : null;

    this.copyMostRelevants.setValue(
      !!mostRelevants &&
        Array.isArray(shops) &&
        Array.isArray(reference) &&
        shops.every((shop) => {
          const ids = mostRelevants[shop];
          return (
            Array.isArray(ids) &&
            reference.every((productId, index) => ids[index] === productId)
          );
        })
    );
    this.copyMostRelevants.markAsPristine();
    this.copyMostRelevants.markAsUntouched();
  }

  private set products(products: Array<Partial<Product>>) {
    this.categoryProducts = products;
    setTimeout(() => this.startMostRelevants(), 250);
  }

  shopsIncludes(shop: string): boolean {
    return this.category.shops.includes(shop);
  }

  getShop(shop: Shops) {
    return this.shops.find((s) => s.value === shop);
  }

  buttonDisabled(shop: Shops): boolean {
    return (
      this.category.mostRelevants &&
      ((!this.mostRelevants[shop]?.length &&
        !this.category?.mostRelevants[shop]) ||
        (!!this.mostRelevants[shop]?.length &&
          this.mostRelevants[shop]?.length ===
            this.category?.mostRelevants[shop]?.length &&
          (this.mostRelevants[shop] as Partial<Product>[])?.every(
            (p, i) => this.category?.mostRelevants[shop][i] === p?.id
          ))) &&
      !this.copyMostRelevants.dirty &&
      !this.copyMostRelevants.touched
    );
  }

  mostRelevant(shop: Shops) {
    const ids = this.category?.mostRelevants?.[shop];
    if (!Array.isArray(ids)) return [];
    return ids.map((id) => this.categoryProducts?.find((cp) => cp.id === id));
  }

  get mostRelevantShops() {
    const shops = this.category?.shops;
    if (!Array.isArray(shops) || !Array.isArray(this.shops)) return [];
    return this.shops.filter((s) => shops.includes(s.value));
  }

  async rebuildCollection(): Promise<void> {
    this.confirmationService.confirm({
      message: `Essa ação irá reconstruir a coleção de produtos, o que pode levar alguns minutos. Deseja continuar?`,
      header: 'Atenção',
      icon: 'pi pi-exclamation-triangle danger',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          LoaderService.showLoader(true);
          await lastValueFrom(
            this.shoppingCategoryService.rebuildCollection(this.category.id)
          );

          this.messageService.add({
            severity: 'success',
            detail: 'Rebuild da coleção iniciado com sucesso.'
          });

          this.messageService.add({
            severity: 'success',
            detail: 'Rebuild da coleção finalizado com sucesso.'
          });
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }

  rebuildMostRelevant() {
    this.confirmationService.confirm({
      message: `Essa ação irá remover todos os produtos da seção de mais relevantes. \n
        Use essa ação quando a seção de mais relevantes não conseguir buscar/alterar a ordem de produtos por causa de produtos com tags desatualizados. Deseja continuar?`,
      header: 'Atenção',
      icon: 'pi pi-exclamation-triangle danger',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          LoaderService.showLoader(true);
          await this.updateCategory({
            id: this.category.id,
            mostRelevants: {
              Glamshop: null,
              mensmarket: null
            }
          });
          this.messageService.add({
            severity: 'success',
            detail: 'Seção de mais relevantes reconstruída com sucesso.'
          });
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }
}
